import { Fragment, useEffect, useState } from 'react';

import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { InfoBox } from '@components/info-box';
import { useIntegrationWebhook } from '@components/integrations';
import { WebhookCondition } from '@components/integrations/integrations-types';
import { Sidebar, hideSidebar } from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useMarketplaceTemplate, useTemplateWebhook } from 'api/app-marketplace/hooks';
import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { ACTION_TYPES } from 'ui/actions/action-config';
import {
  AppNotConnectedSection,
  DisabledSection,
  SidebarWarningSection,
} from 'ui/actions/integration/common';
import {
  ActionHeader,
  ActionSidebarLoading,
  SidebarProps,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import {
  WebhookConditionComponent,
  WebhookConditionData,
  WebhookNotSubscribedSection,
} from './common';

import { getFieldMappingId } from '../helpers';

const ACTION_TYPE = ACTION_TYPES.webhook;

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const [isTemplate, setIsTemplate] = useState(false);
  const templateWebhookId = data?.values?.templateWebhookId;
  const integrationWebhookId = data?.values?.integrationWebhookId?.value;

  useEffect(() => {
    if (templateWebhookId && !integrationWebhookId) {
      setIsTemplate(true);
    } else if (integrationWebhookId) {
      setIsTemplate(false);
    }
  }, [templateWebhookId, integrationWebhookId]);

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return isTemplate && templateWebhookId ? (
    <TemplateSidebar templateWebhookId={templateWebhookId} {...props} />
  ) : (
    <WebhookSidebar {...props} />
  );
};

const WebhookSidebar = (props: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction<typeof ACTION_TYPE>(journeyActionId);
  const values = data?.values;
  const webhookId = data?.values?.integrationWebhookId?.value ?? '';
  const editMutation = useUpdateJourneyAction(journeyActionId);
  const {
    data: webhookData,
    isLoading: webhookIsLoading,
    isError: webhookIsError,
  } = useIntegrationWebhook(webhookId);

  const { data: template, isLoading: templateLoading } = useMarketplaceTemplate(
    webhookData?.templateId ?? ''
  );
  const hasAuthError = Boolean(
    webhookData?.templateId && template?.connectionStatus !== 'connected'
  );
  const hasSubscribeError = Boolean(
    !hasAuthError && webhookData?.isSubscribable && webhookData?.subscribeValid === false
  );

  const fieldMappingId = getFieldMappingId(data?.journey ?? '');

  const baseVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['email', 'text', 'select', 'multi_select', 'phone', 'date', 'separator', 'boolean'],
  });

  if (isLoading || webhookIsLoading || !data || (webhookData?.templateId && templateLoading)) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  const conditions = (webhookData?.conditions ?? []).filter((condition) => condition.visible);
  const conditionDefaultData = values?.conditionsData ?? {};

  const handleSubmit = async (data: WebhookConditionData, description: string) => {
    editMutation.mutateAsync({
      isAutomatic: true,
      action: ACTION_TYPE,
      description: description,
      values: {
        ...values,
        conditionsData: data,
      },
    });
  };

  const Container = hasAuthError || hasSubscribeError ? DisabledSection : Fragment;

  return (
    <Sidebar>
      <ActionHeader
        actionType={ACTION_TYPE}
        title={webhookData?.name}
        subtitle={webhookData?.integrationName}
        subtitleFont="regular12"
        subtitleLocation="below"
        customIcon={
          webhookData?.thumbnail ? <SquareImage src={webhookData.thumbnail} /> : undefined
        }
        {...props}
      />
      <Sidebar.Content>
        {webhookIsError ? (
          <InfoBox variant="red" icon={<IconFormIndicatorError />} text={t('webhookError')} />
        ) : (
          <Stack orientation="vertical" space={6}>
            {hasAuthError && (
              <SidebarWarningSection noMargin>
                <AppNotConnectedSection template={template} journeyActionId={journeyActionId} />
              </SidebarWarningSection>
            )}
            {hasSubscribeError && (
              <SidebarWarningSection noMargin>
                <WebhookNotSubscribedSection webhookShortId={webhookData?.shortId ?? ''} />
              </SidebarWarningSection>
            )}
            <Container>
              <WebhookConditionComponent
                integrationId={webhookData?.integration ?? ''}
                initialConditions={conditions}
                variableGroups={baseVariableGroups}
                defaultData={conditionDefaultData}
                onCancel={hideSidebar}
                onSubmit={handleSubmit}
                fieldMappingId={fieldMappingId}
                description={data.description}
              />
            </Container>
          </Stack>
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

const TemplateSidebar = ({
  templateWebhookId,
  ...props
}: SidebarProps & { templateWebhookId: string }) => {
  const { data: webhookData, isLoading: webhookLoading } = useTemplateWebhook(templateWebhookId);
  const { data: template, isLoading: templateLoading } = useMarketplaceTemplate(
    webhookData?.templateId ?? ''
  );
  const conditions = (webhookData?.conditions ?? []).filter((condition) => condition.visible);

  if (webhookLoading || !webhookData || templateLoading) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader
        actionType={ACTION_TYPE}
        title={webhookData?.name}
        subtitle={webhookData?.integrationName}
        subtitleFont="regular12"
        subtitleLocation="below"
        customIcon={
          webhookData?.thumbnail ? <SquareImage src={webhookData.thumbnail} /> : undefined
        }
        {...props}
      />
      <Sidebar.Content>
        <Stack space={6}>
          <SidebarWarningSection noMargin>
            <AppNotConnectedSection template={template} journeyActionId={props.journeyActionId} />
          </SidebarWarningSection>
          <DisabledSection>
            <WebhookConditionComponent
              integrationId={webhookData?.integration ?? ''}
              initialConditions={conditions as WebhookCondition[]}
              variableGroups={[]}
              defaultData={{}}
              onCancel={hideSidebar}
              onSubmit={async () => {}}
              fieldMappingId=""
              description=""
            />
          </DisabledSection>
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};
const sidebarLocale = {
  en: {
    value: 'Value',
    webhookError:
      'Failed to load webhook, if the webhook has been deleted please replace the action in the journey builder',
  },
};
